/**
 * Columna "Confianza" de los demos, IGUAL a la de la pantalla de cotización.
 *
 * Los textos, etiquetas y colores son COPIA de `routes/QuotePage.tsx` (`CONFIANZA_LABEL`,
 * `CONFIANZA_AYUDA`, `CONFIANZA_ACCENT`, `CONFIANZA_ORDEN` y `LeyendaConfianza`). Allá no se exportan y
 * ese archivo no se toca desde los demos; si cambian allá, hay que copiarlos otra vez aquí.
 */
import { Popover, PopoverSurface, PopoverTrigger } from '@fluentui/react-components';

export type ConfKind = 'alta' | 'media' | 'sin_equivalente' | 'sintetica';

export const CONFIANZA_ACCENT: Record<ConfKind, string> = {
  alta: 'var(--ok)',
  media: 'var(--warn)',
  sin_equivalente: 'var(--bad)',
  sintetica: 'var(--privado)',
};

export const CONFIANZA_LABEL: Record<ConfKind, string> = {
  alta: 'Alta',
  media: 'Media',
  sin_equivalente: 'Sin equivalente',
  sintetica: 'Precio analizado con IA',
};

export const CONFIANZA_AYUDA: Record<ConfKind, { que: string; hacer: string }> = {
  alta: {
    que: 'Se encontró en tu catálogo un concepto equivalente y el sistema está seguro de que corresponde. El precio sale de tu catálogo.',
    hacer: 'Puedes usarlo tal cual; revísalo solo si quieres confirmarlo.',
  },
  media: {
    que: 'Se encontró un concepto parecido, pero el sistema no está seguro de que sea el mismo. El precio sale de tu catálogo.',
    hacer: 'Revísalo antes de usarlo: confirma que el equivalente corresponde; si no, analízalo con IA para que arme el precio.',
  },
  sin_equivalente: {
    que: 'No se encontró en tu catálogo un concepto equivalente, así que el renglón todavía no tiene precio.',
    hacer: 'Analízalo con IA para que arme el precio.',
  },
  sintetica: {
    que: 'El precio lo armó la inteligencia artificial porque no había un equivalente en tu catálogo.',
    hacer: 'Revísalo y apruébalo (o recházalo); si no te convence, ajusta el contexto de proyecto y vuelve a generarlo.',
  },
};

export const CONFIANZA_ORDEN: ConfKind[] = ['alta', 'media', 'sin_equivalente', 'sintetica'];

export function LeyendaConfianza() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
      <div style={{ fontWeight: 700 }}>Qué tan seguro está el sistema del precio de cada renglón:</div>
      {CONFIANZA_ORDEN.map((k) => (
        <div key={k} style={{ display: 'flex', gap: '7px' }}>
          <span style={{ flexShrink: 0, width: 8, height: 8, borderRadius: '50%', marginTop: '5px', background: CONFIANZA_ACCENT[k] }} />
          <div>
            <span style={{ fontWeight: 700 }}>{CONFIANZA_LABEL[k] ?? k}</span> — {CONFIANZA_AYUDA[k].que}{' '}
            <span style={{ color: 'var(--text-tertiary)' }}>{CONFIANZA_AYUDA[k].hacer}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Encabezado "Confianza ?" con la misma explicación que la cotización. */
export function EncabezadoConfianza() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
      Confianza
      <Popover withArrow positioning="below">
        <PopoverTrigger disableButtonEnhancement>
          <span
            role="button"
            tabIndex={0}
            aria-label="Qué significa cada nivel de confianza"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 19,
              height: 19,
              borderRadius: '50%',
              border: '1.5px solid var(--border-default)',
              color: 'var(--text-tertiary)',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              background: 'var(--bg-surface)',
            }}
          >
            ?
          </span>
        </PopoverTrigger>
        <PopoverSurface>
          <div style={{ maxWidth: 340, fontSize: 'var(--fs-200)', lineHeight: 1.45, color: 'var(--text-primary)' }}>
            <LeyendaConfianza />
          </div>
        </PopoverSurface>
      </Popover>
    </span>
  );
}

/** Pastilla del nivel en el renglón: la etiqueta y, debajo, qué hacer en llano. */
export function PastillaConfianza({ k }: { k: ConfKind }) {
  return (
    <div title={`${CONFIANZA_AYUDA[k].que} ${CONFIANZA_AYUDA[k].hacer}`}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, whiteSpace: 'nowrap' }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: CONFIANZA_ACCENT[k] }} />
        {CONFIANZA_LABEL[k]}
      </span>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', maxWidth: 220 }}>{CONFIANZA_AYUDA[k].hacer}</div>
    </div>
  );
}
